// Sample email data for demo
import type { Email, Contact, EmailThread } from '../types/email';

export const sampleContacts: Contact[] = [
  {
    id: '1',
    email: 'sarah.chen@techcorp.com',
    name: 'Sarah Chen',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah',
    company: 'TechCorp',
    role: 'Product Manager',
    intelligence: {
      communicationStyle: 'professional',
      responseTime: 'quick',
      topics: ['product roadmap', 'user feedback', 'sprint planning'],
      sentiment: 85,
      lastInteraction: new Date('2025-01-09'),
      interactionCount: 47,
      preferredChannel: 'email',
      timezone: 'PST',
      language: 'en'
    },
    relationship: {
      strength: 85,
      type: 'colleague',
      duration: 365,
      frequency: 12
    }
  },
  {
    id: '2',
    email: 'michael.johnson@design.studio',
    name: 'Michael Johnson',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Michael',
    company: 'Design Studio',
    role: 'Creative Director',
    intelligence: {
      communicationStyle: 'casual',
      responseTime: 'normal',
      topics: ['design reviews', 'branding', 'UI/UX'],
      sentiment: 75,
      lastInteraction: new Date('2025-01-08'),
      interactionCount: 23,
      preferredChannel: 'chat',
      timezone: 'EST'
    },
    relationship: {
      strength: 65,
      type: 'client',
      duration: 180,
      frequency: 8
    }
  },
  {
    id: '3',
    email: 'emily.davis@startup.io',
    name: 'Emily Davis',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Emily',
    company: 'Startup.io',
    role: 'CEO',
    intelligence: {
      communicationStyle: 'friendly',
      responseTime: 'immediate',
      topics: ['partnerships', 'investment', 'growth strategy'],
      sentiment: 90,
      lastInteraction: new Date('2025-01-09'),
      interactionCount: 89,
      preferredChannel: 'email'
    },
    relationship: {
      strength: 95,
      type: 'colleague',
      duration: 730,
      frequency: 20
    }
  }
];

export const sampleEmails: Email[] = [
  {
    id: 'email-1',
    threadId: 'thread-1',
    from: sampleContacts[0],
    to: [sampleContacts[2]],
    cc: [],
    subject: '🚨 Critical: Production deployment needs approval',
    snippet: 'Hi team, We need immediate approval for the production deployment scheduled for tonight...',
    body: `Hi team,

We need immediate approval for the production deployment scheduled for tonight at 11 PM PST. The release includes:

• Security patch for authentication module
• Performance improvements (30% faster load times)
• Bug fixes for mobile app crashes
• New analytics dashboard

The staging environment has been thoroughly tested and all automated tests are passing. Please review the deployment checklist attached and provide your approval by 5 PM.

Action required: Reply with your approval or concerns.

Best regards,
Sarah`,
    htmlBody: `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; line-height: 1.6; color: hsl(var(--foreground));">
        <p>Hi team,</p>
        
        <p>We need <strong>immediate approval</strong> for the production deployment scheduled for tonight at <strong>11 PM PST</strong>. The release includes:</p>
        
        <ul style="margin: 16px 0; padding-left: 24px;">
          <li>Security patch for authentication module</li>
          <li>Performance improvements (30% faster load times)</li>
          <li>Bug fixes for mobile app crashes</li>
          <li>New analytics dashboard</li>
        </ul>
        
        <p>The staging environment has been thoroughly tested and all automated tests are passing. Please review the deployment checklist attached and provide your approval by <strong>5 PM</strong>.</p>
        
        <p style="background: hsl(var(--color-surface)); color: hsl(var(--color-secondary-foreground)); padding: 12px; border-radius: 8px; margin: 16px 0;"><strong>Action required:</strong> Reply with your approval or concerns.</p>
        
        <p>Best regards,<br>Sarah</p>
      </div>
    `,
    attachments: [
      {
        id: 'attach-1',
        filename: 'deployment-checklist.pdf',
        mimeType: 'application/pdf',
        size: 245000,
        url: '#'
      }
    ],
    date: new Date('2025-01-09T14:30:00'),
    isRead: false,
    isStarred: true,
    labels: [
      { id: 'label-1', name: 'urgent', color: '#EF4444', aiGenerated: true },
      { id: 'label-2', name: 'deployment', color: '#3B82F6', aiGenerated: true }
    ],
    category: 'urgent',
    priority: 'critical',
    sentiment: 'urgent',
    actionRequired: true,
    suggestedActions: [
      {
        id: 'action-1',
        type: 'reply',
        title: 'Approve deployment',
        description: 'Send approval for production deployment',
        draftContent: 'Approved for deployment. All items on the checklist have been verified.'
      },
      {
        id: 'action-2',
        type: 'schedule',
        title: 'Schedule review meeting',
        description: 'Set up deployment review for 4 PM',
        dueDate: new Date('2025-01-09T16:00:00')
      }
    ],
    smartSummary: 'Critical production deployment tonight needs immediate approval. Includes security patches and performance improvements.',
    extractedEntities: [
      { type: 'date', value: '11 PM PST tonight', context: 'deployment time', confidence: 0.95 },
      { type: 'date', value: '5 PM', context: 'approval deadline', confidence: 0.9 },
      { type: 'task', value: 'Review deployment checklist', context: 'action required', confidence: 0.85 }
    ],
    aiScore: 95,
    personalContext: {
      relatedProjects: ['Q1 Release', 'Security Audit'],
      previousConversations: ['thread-prev-1', 'thread-prev-2'],
      sharedFiles: ['roadmap.pdf', 'security-report.docx'],
      meetings: [],
      notes: 'Sarah always sends deployment requests with detailed checklists'
    }
  },
  {
    id: 'email-2',
    threadId: 'thread-2',
    from: sampleContacts[1],
    to: [sampleContacts[2]],
    cc: [],
    subject: 'Re: New brand identity concepts',
    snippet: 'Hey! Just finished the new brand identity concepts. I think you\'re going to love the direction...',
    body: `Hey!

Just finished the new brand identity concepts. I think you're going to love the direction we've taken. The designs embrace a modern, minimalist aesthetic while maintaining the warmth and approachability that's core to your brand.

Key highlights:
- New color palette: Deep purple (hsl(var(--color-accent))) with vibrant accents
- Updated typography: Clean, readable, with personality
- Flexible logo system that works across all touchpoints
- Motion design principles for digital experiences

I've attached the presentation deck with all the concepts. Would love to walk you through them in detail. Are you free for a call tomorrow afternoon?

Also, I noticed the team mentioned wanting to see more variations of the logo. I've included 12 different options in the deck, ranging from conservative updates to more bold reimaginings.

Let me know your thoughts!

Cheers,
Michael`,
    htmlBody: `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; line-height: 1.6; color: hsl(var(--foreground));">
        <p>Hey!</p>
        
        <p>Just finished the <strong>new brand identity concepts</strong>. I think you're going to love the direction we've taken. The designs embrace a modern, minimalist aesthetic while maintaining the warmth and approachability that's core to your brand.</p>
        
        <h3 style="color: hsl(var(--color-primary)); margin-top: 24px;">Key highlights:</h3>
        <ul style="margin: 16px 0; padding-left: 24px;">
          <li>New color palette: <span style="color: hsl(var(--color-accent)); font-weight: 600;">Deep purple (hsl(var(--color-accent)))</span> with vibrant accents</li>
          <li>Updated typography: Clean, readable, with personality</li>
          <li>Flexible logo system that works across all touchpoints</li>
          <li>Motion design principles for digital experiences</li>
        </ul>
        
        <p>I've attached the presentation deck with all the concepts. Would love to walk you through them in detail. Are you free for a call tomorrow afternoon?</p>
        
        <p>Also, I noticed the team mentioned wanting to see more variations of the logo. I've included <strong>12 different options</strong> in the deck, ranging from conservative updates to more bold reimaginings.</p>
        
        <p>Let me know your thoughts!</p>
        
        <p>Cheers,<br>Michael</p>
      </div>
    `,
    attachments: [
      {
        id: 'attach-2',
        filename: 'brand-identity-v2.pdf',
        mimeType: 'application/pdf',
        size: 8500000,
        url: '#'
      },
      {
        id: 'attach-3',
        filename: 'logo-variations.ai',
        mimeType: 'application/illustrator',
        size: 3200000,
        url: '#'
      }
    ],
    date: new Date('2025-01-08T10:15:00'),
    isRead: true,
    isStarred: false,
    labels: [
      { id: 'label-3', name: 'design', color: '#8B5CF6', aiGenerated: false },
      { id: 'label-4', name: 'branding', color: '#EC4899', aiGenerated: true }
    ],
    category: 'work',
    priority: 'high',
    sentiment: 'positive',
    actionRequired: true,
    suggestedActions: [
      {
        id: 'action-3',
        type: 'schedule',
        title: 'Schedule design review',
        description: 'Book time to review brand concepts',
        dueDate: new Date('2025-01-10T14:00:00')
      },
      {
        id: 'action-4',
        type: 'reply',
        title: 'Provide initial feedback',
        description: 'Share thoughts on the concepts',
        draftContent: 'Thanks Michael! The concepts look fantastic. I particularly love the color palette. Tomorrow afternoon works - how about 2 PM?'
      }
    ],
    smartSummary: 'New brand identity concepts ready with modern design, new colors, and 12 logo variations. Review call requested.',
    extractedEntities: [
      { type: 'task', value: 'Review brand identity concepts', context: 'action needed', confidence: 0.9 },
      { type: 'organization', value: 'Design Studio', context: 'sender company', confidence: 0.85 }
    ],
    aiScore: 75
  },
  {
    id: 'email-3',
    threadId: 'thread-3',
    from: sampleContacts[2],
    to: [sampleContacts[0]],
    cc: [],
    subject: 'Investor meeting prep - need your input',
    snippet: 'Sarah, I need your expertise for our Series B pitch deck. The investors are particularly interested in...',
    body: `Sarah,

I need your expertise for our Series B pitch deck. The investors are particularly interested in our technical roadmap and scalability plans.

Could you help me prepare:
1. Technical architecture overview (current vs. future state)
2. Scalability metrics and projections
3. Security and compliance roadmap
4. Team growth plans for engineering

The meeting is next Tuesday at 10 AM. I know it's short notice, but your input would be invaluable. I've scheduled some time on your calendar for Monday to go over everything.

Also, they've specifically asked about our AI integration timeline - I remember you mentioned some exciting developments in that area?

Thanks for always being such a great partner in these initiatives!

Best,
Emily`,
    htmlBody: `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; line-height: 1.6; color: hsl(var(--foreground));">
        <p>Sarah,</p>
        
        <p>I need your expertise for our <strong>Series B pitch deck</strong>. The investors are particularly interested in our technical roadmap and scalability plans.</p>
        
        <p>Could you help me prepare:</p>
        <ol style="margin: 16px 0; padding-left: 24px;">
          <li>Technical architecture overview (current vs. future state)</li>
          <li>Scalability metrics and projections</li>
          <li>Security and compliance roadmap</li>
          <li>Team growth plans for engineering</li>
        </ol>
        
        <p>The meeting is <strong style="color: hsl(var(--color-primary));">next Tuesday at 10 AM</strong>. I know it's short notice, but your input would be invaluable. I've scheduled some time on your calendar for Monday to go over everything.</p>
        
        <p style="background: hsl(var(--color-surface)); color: hsl(var(--color-secondary-foreground)); padding: 12px; border-radius: 8px; margin: 16px 0;">Also, they've specifically asked about our <strong>AI integration timeline</strong> - I remember you mentioned some exciting developments in that area?</p>
        
        <p>Thanks for always being such a great partner in these initiatives!</p>
        
        <p>Best,<br>Emily</p>
      </div>
    `,
    attachments: [],
    date: new Date('2025-01-09T09:00:00'),
    isRead: true,
    isStarred: true,
    labels: [
      { id: 'label-5', name: 'important', color: '#F59E0B', aiGenerated: true },
      { id: 'label-6', name: 'investor-relations', color: '#10B981', aiGenerated: false }
    ],
    category: 'important',
    priority: 'high',
    sentiment: 'positive',
    actionRequired: true,
    suggestedActions: [
      {
        id: 'action-5',
        type: 'reply',
        title: 'Confirm availability',
        description: 'Confirm Monday prep session',
        draftContent: 'Absolutely! Monday works perfectly. I\'ll prepare the technical sections over the weekend.'
      },
      {
        id: 'action-6',
        type: 'followup',
        title: 'Prepare materials',
        description: 'Create technical roadmap slides',
        dueDate: new Date('2025-01-13T09:00:00')
      }
    ],
    smartSummary: 'Help needed for Series B pitch deck - technical roadmap, scalability, and AI integration. Meeting Tuesday.',
    extractedEntities: [
      { type: 'date', value: 'next Tuesday at 10 AM', context: 'investor meeting', confidence: 0.95 },
      { type: 'date', value: 'Monday', context: 'prep session', confidence: 0.9 },
      { type: 'task', value: 'Prepare technical architecture overview', context: 'deliverable', confidence: 0.85 }
    ],
    aiScore: 85
  },
  {
    id: 'email-4',
    threadId: 'thread-4',
    from: { ...sampleContacts[0], email: 'newsletter@techweekly.com', name: 'TechWeekly' },
    to: [sampleContacts[2]],
    cc: [],
    subject: 'This Week in Tech: AI Breakthroughs & Market Updates',
    snippet: 'Your weekly digest of the most important tech news, curated for leaders and innovators...',
    body: `Your weekly digest of the most important tech news...

TOP STORIES:
• OpenAI announces GPT-5 with reasoning capabilities
• Apple's Vision Pro hits 1 million units sold
• EU passes landmark AI regulation framework
• Quantum computing breakthrough at IBM

MARKET UPDATES:
• Tech stocks rally on strong earnings
• VC funding reaches $50B in Q1
• Startup acquisitions up 40% YoY

FEATURED ARTICLE:
"The Future of Remote Work: 2025 Trends"
Read more at techweekly.com/future-of-work`,
    htmlBody: `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; line-height: 1.6; color: hsl(var(--foreground)); max-width: 600px;">
        <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 32px; text-align: center; border-radius: 12px 12px 0 0;">
          <h1 style="color: white; margin: 0; font-size: 28px;">This Week in Tech</h1>
          <p style="color: rgba(255,255,255,0.9); margin: 8px 0 0;">Your weekly digest of the most important tech news</p>
        </div>
        
        <div style="background: #1a1a1a; padding: 24px; border-radius: 0 0 12px 12px;">
          <h2 style="color: hsl(var(--color-primary)); font-size: 20px; margin-top: 0;">🔥 TOP STORIES</h2>
          <ul style="margin: 16px 0; padding-left: 24px; line-height: 1.8;">
            <li><strong>OpenAI</strong> announces GPT-5 with reasoning capabilities</li>
            <li><strong>Apple's Vision Pro</strong> hits 1 million units sold</li>
            <li><strong>EU</strong> passes landmark AI regulation framework</li>
            <li><strong>Quantum computing</strong> breakthrough at IBM</li>
          </ul>
          
          <h2 style="color: hsl(var(--color-primary)); font-size: 20px; margin-top: 32px;">📈 MARKET UPDATES</h2>
          <ul style="margin: 16px 0; padding-left: 24px; line-height: 1.8;">
            <li>Tech stocks rally on strong earnings</li>
            <li>VC funding reaches <strong>$50B</strong> in Q1</li>
            <li>Startup acquisitions up <strong>40% YoY</strong></li>
          </ul>
          
          <div style="background: #2a2a2a; padding: 20px; border-radius: 8px; margin-top: 32px; border-left: 4px solid hsl(var(--color-primary));">
            <h3 style="color: hsl(var(--color-primary)); margin-top: 0; font-size: 18px;">📰 FEATURED ARTICLE</h3>
            <p style="margin: 8px 0; font-size: 16px;"><strong>"The Future of Remote Work: 2025 Trends"</strong></p>
            <a href="https://techweekly.com/future-of-work" style="color: #3b82f6; text-decoration: none; font-weight: 600;">Read more →</a>
          </div>
        </div>
      </div>
    `,
    attachments: [],
    date: new Date('2025-01-07T06:00:00'),
    isRead: true,
    isStarred: false,
    labels: [
      { id: 'label-7', name: 'newsletter', color: '#6B7280', aiGenerated: true }
    ],
    category: 'newsletter',
    priority: 'low',
    sentiment: 'neutral',
    actionRequired: false,
    suggestedActions: [
      {
        id: 'action-7',
        type: 'archive',
        title: 'Archive newsletter',
        description: 'Move to archive after reading'
      }
    ],
    smartSummary: 'Tech weekly newsletter with AI news, market updates, and remote work trends.',
    extractedEntities: [
      { type: 'organization', value: 'OpenAI', context: 'GPT-5 announcement', confidence: 0.9 },
      { type: 'organization', value: 'Apple', context: 'Vision Pro sales', confidence: 0.9 },
      { type: 'money', value: '$50B', context: 'VC funding Q1', confidence: 0.85 }
    ],
    aiScore: 25
  },
  {
    id: 'email-5',
    threadId: 'thread-5',
    from: { ...sampleContacts[1], email: 'support@zoom.us', name: 'Zoom' },
    to: [sampleContacts[2]],
    cc: [],
    subject: 'Your meeting recording is ready',
    snippet: 'The recording for "Q1 Planning Session" is now available to view...',
    body: `The recording for "Q1 Planning Session" is now available to view.

Meeting Details:
- Date: January 8, 2025
- Duration: 1 hour 23 minutes
- Participants: 12
- Host: Emily Davis

Access your recording: https://zoom.us/recording/abc123

The recording will be available for 30 days. Download it if you need permanent access.

Auto-generated transcript is also available.`,
    attachments: [],
    date: new Date('2025-01-08T15:45:00'),
    isRead: false,
    isStarred: false,
    labels: [
      { id: 'label-8', name: 'transactional', color: '#6B7280', aiGenerated: true }
    ],
    category: 'transactional',
    priority: 'low',
    sentiment: 'neutral',
    actionRequired: false,
    suggestedActions: [
      {
        id: 'action-8',
        type: 'archive',
        title: 'Archive after viewing',
        description: 'Archive once recording is reviewed'
      }
    ],
    smartSummary: 'Zoom recording available for Q1 Planning Session (1h 23m, 12 participants).',
    extractedEntities: [
      { type: 'date', value: 'January 8, 2025', context: 'meeting date', confidence: 0.95 },
      { type: 'link', value: 'https://zoom.us/recording/abc123', context: 'recording link', confidence: 1.0 }
    ],
    aiScore: 20
  }
];

export const sampleThreads: EmailThread[] = [
  {
    id: 'thread-1',
    subject: 'Production deployment needs approval',
    participants: [sampleContacts[0], sampleContacts[2]],
    emails: [sampleEmails[0]],
    lastMessageDate: new Date('2025-01-09T14:30:00'),
    messageCount: 1,
    isImportant: true,
    aiSummary: 'Critical deployment approval needed for tonight\'s release with security patches.'
  },
  {
    id: 'thread-2',
    subject: 'New brand identity concepts',
    participants: [sampleContacts[1], sampleContacts[2]],
    emails: [sampleEmails[1]],
    lastMessageDate: new Date('2025-01-08T10:15:00'),
    messageCount: 1,
    isImportant: false,
    aiSummary: 'Brand identity redesign with new colors and logo variations ready for review.'
  }
];

// Sample calendar events for demo
import type { CalendarEvent } from '../types/calendar';

// Generate sample calendar events with proper date distribution across the entire current month
const generateSampleCalendarEvents = (): CalendarEvent[] => {
  const today = new Date();
  const events: CalendarEvent[] = [];
  
  // Today's events
  events.push({
    id: 'cal-demo-1',
    userId: 'demo-user',
    title: 'Team Standup',
    description: 'Daily sync with the engineering team',
    startTime: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 9, 0, 0, 0),
    endTime: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 9, 30, 0, 0),
    eventType: 'meeting',
    location: 'Zoom: https://zoom.us/j/123456789',
    attendees: ['sarah.chen@techcorp.com', 'michael.johnson@design.studio', 'team@company.com'],
    isAllDay: false,
    color: '#3B82F6',
    category: 'Work',
    createdAt: new Date(),
    updatedAt: new Date()
  });

  events.push({
    id: 'cal-demo-2',
    userId: 'demo-user',
    title: 'Product Demo with Client',
    description: 'Showcase new features and gather feedback',
    startTime: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 14, 0, 0, 0),
    endTime: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 15, 0, 0, 0),
    eventType: 'meeting',
    location: 'Google Meet',
    attendees: ['client@example.com', 'sales@company.com'],
    isAllDay: false,
    reminderMinutes: 15,
    color: '#10B981',
    category: 'Work',
    createdAt: new Date(),
    updatedAt: new Date()
  });

  events.push({
    id: 'cal-demo-3',
    userId: 'demo-user',
    title: 'Code Review Deadline',
    description: 'Review and merge all pending PRs',
    startTime: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 17, 0, 0, 0),
    endTime: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 17, 30, 0, 0),
    eventType: 'reminder',
    isAllDay: false,
    reminderMinutes: 30,
    color: '#F59E0B',
    category: 'Work',
    createdAt: new Date(),
    updatedAt: new Date()
  });

  // Tomorrow's events
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  
  events.push({
    id: 'cal-demo-4',
    userId: 'demo-user',
    title: 'Quarterly Planning',
    description: 'Q2 roadmap and goals discussion',
    startTime: new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 10, 0, 0, 0),
    endTime: new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 12, 0, 0, 0),
    eventType: 'meeting',
    location: 'Conference Room A',
    attendees: ['leadership@company.com', 'product@company.com'],
    isAllDay: false,
    color: '#8B5CF6',
    category: 'Work',
    createdAt: new Date(),
    updatedAt: new Date()
  });

  events.push({
    id: 'cal-demo-5',
    userId: 'demo-user',
    title: 'Update Documentation',
    description: 'Finish API documentation for new endpoints',
    startTime: new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 15, 0, 0, 0),
    endTime: new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 16, 0, 0, 0),
    eventType: 'task',
    isAllDay: false,
    color: '#EC4899',
    category: 'Work',
    createdAt: new Date(),
    updatedAt: new Date()
  });

  // Day after tomorrow
  const dayAfter = new Date(today);
  dayAfter.setDate(today.getDate() + 2);
  
  events.push({
    id: 'cal-demo-6',
    userId: 'demo-user',
    title: 'Fitness - Morning Run',
    description: '5K run in the park',
    startTime: new Date(dayAfter.getFullYear(), dayAfter.getMonth(), dayAfter.getDate(), 7, 0, 0, 0),
    endTime: new Date(dayAfter.getFullYear(), dayAfter.getMonth(), dayAfter.getDate(), 8, 0, 0, 0),
    eventType: 'event',
    location: 'Central Park',
    isAllDay: false,
    color: '#06B6D4',
    category: 'Personal',
    createdAt: new Date(),
    updatedAt: new Date()
  });

  // Add more events across the current month for a rich demo experience
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  
  // Week 1 - Add events on various days
  events.push({
    id: 'cal-demo-7',
    userId: 'demo-user',
    title: 'Design Review',
    description: 'Review new UI mockups with design team',
    startTime: new Date(currentYear, currentMonth, 5, 14, 0, 0, 0),
    endTime: new Date(currentYear, currentMonth, 5, 15, 30, 0, 0),
    eventType: 'meeting',
    location: 'Zoom',
    attendees: ['design@company.com'],
    isAllDay: false,
    color: '#F59E0B',
    category: 'Work',
    createdAt: new Date(),
    updatedAt: new Date()
  });

  // Week 2
  events.push({
    id: 'cal-demo-8',
    userId: 'demo-user',
    title: 'Client Presentation',
    description: 'Present Q1 results and Q2 roadmap',
    startTime: new Date(currentYear, currentMonth, 12, 10, 0, 0, 0),
    endTime: new Date(currentYear, currentMonth, 12, 11, 30, 0, 0),
    eventType: 'meeting',
    location: 'Conference Room B',
    attendees: ['client@example.com', 'sales@company.com'],
    isAllDay: false,
    color: '#14B8A6',
    category: 'Work',
    createdAt: new Date(),
    updatedAt: new Date()
  });

  events.push({
    id: 'cal-demo-9',
    userId: 'demo-user',
    title: 'Team Lunch',
    description: 'Monthly team building lunch',
    startTime: new Date(currentYear, currentMonth, 15, 12, 0, 0, 0),
    endTime: new Date(currentYear, currentMonth, 15, 13, 30, 0, 0),
    eventType: 'event',
    location: 'Italian Restaurant',
    isAllDay: false,
    color: '#EC4899',
    category: 'Personal',
    createdAt: new Date(),
    updatedAt: new Date()
  });

  // Week 3
  events.push({
    id: 'cal-demo-10',
    userId: 'demo-user',
    title: 'Sprint Retrospective',
    description: 'Review sprint performance and improvements',
    startTime: new Date(currentYear, currentMonth, 18, 15, 0, 0, 0),
    endTime: new Date(currentYear, currentMonth, 18, 16, 0, 0, 0),
    eventType: 'meeting',
    location: 'Zoom',
    attendees: ['team@company.com'],
    isAllDay: false,
    color: '#6366F1',
    category: 'Work',
    createdAt: new Date(),
    updatedAt: new Date()
  });

  events.push({
    id: 'cal-demo-11',
    userId: 'demo-user',
    title: 'Dentist Appointment',
    description: 'Regular checkup',
    startTime: new Date(currentYear, currentMonth, 20, 9, 0, 0, 0),
    endTime: new Date(currentYear, currentMonth, 20, 10, 0, 0, 0),
    eventType: 'reminder',
    location: 'Downtown Dental',
    isAllDay: false,
    reminderMinutes: 60,
    color: '#06B6D4',
    category: 'Personal',
    createdAt: new Date(),
    updatedAt: new Date()
  });

  // Week 4
  events.push({
    id: 'cal-demo-12',
    userId: 'demo-user',
    title: 'Budget Review',
    description: 'Monthly department budget review',
    startTime: new Date(currentYear, currentMonth, 25, 13, 0, 0, 0),
    endTime: new Date(currentYear, currentMonth, 25, 14, 0, 0, 0),
    eventType: 'meeting',
    location: 'Conference Room A',
    attendees: ['finance@company.com', 'management@company.com'],
    isAllDay: false,
    color: '#8B5CF6',
    category: 'Work',
    createdAt: new Date(),
    updatedAt: new Date()
  });

  events.push({
    id: 'cal-demo-13',
    userId: 'demo-user',
    title: 'Submit Expense Reports',
    description: 'Deadline for monthly expense submissions',
    startTime: new Date(currentYear, currentMonth, 28, 17, 0, 0, 0),
    endTime: new Date(currentYear, currentMonth, 28, 17, 30, 0, 0),
    eventType: 'task',
    isAllDay: false,
    reminderMinutes: 120,
    color: '#F59E0B',
    category: 'Work',
    createdAt: new Date(),
    updatedAt: new Date()
  });

  // Add next week's events for continuity
  for (let i = 3; i < 7; i++) {
    const futureDate = new Date(today);
    futureDate.setDate(today.getDate() + i);
    
    events.push({
      id: `cal-demo-week-${i}`,
      userId: 'demo-user',
      title: i % 2 === 0 ? 'Client Check-in' : 'Sprint Planning',
      description: i % 2 === 0 ? 'Weekly sync with key clients' : 'Plan next sprint deliverables',
      startTime: new Date(futureDate.getFullYear(), futureDate.getMonth(), futureDate.getDate(), 11, 0, 0, 0),
      endTime: new Date(futureDate.getFullYear(), futureDate.getMonth(), futureDate.getDate(), 12, 0, 0, 0),
      eventType: 'meeting',
      location: 'Zoom',
      attendees: ['team@company.com'],
      isAllDay: false,
      color: i % 2 === 0 ? '#14B8A6' : '#6366F1',
      category: 'Work',
      createdAt: new Date(),
      updatedAt: new Date()
    });
  }

  return events;
};

export const sampleCalendarEvents: CalendarEvent[] = generateSampleCalendarEvents();